import { useEffect, useRef, useState } from "react";
import { Lock, MoreHorizontal, MoreVertical, Pencil, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { useChatStore } from "@/features/chat/store/chat-store";
import type { Conversation, MemberRole, Message } from "@/features/chat/types";
import { conversationTitle, formatTime, initials, otherMember } from "@/features/chat/utils";
import { GroupSettingsDialog } from "@/features/chat/components/group-settings-dialog";
import { PresenceDot } from "@/features/chat/components/presence-dot";
import { cn } from "@/lib/utils";

/** Calendar-day key (local) for grouping messages under date dividers. */
function dayKey(iso: string): string {
  return new Date(iso).toDateString();
}

/** Human divider label: Today / Yesterday / weekday / "Mon D". */
function dayLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 6);
  if (date >= weekAgo) return date.toLocaleDateString([], { weekday: "long" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function DayDivider({ label }: { label: string }) {
  return (
    <div className="text-muted-foreground flex items-center gap-3 py-1 text-[11px] font-semibold tracking-[0.08em] uppercase">
      <span className="bg-border h-px flex-1" />
      {label}
      <span className="bg-border h-px flex-1" />
    </div>
  );
}

function TypingBubble({ conversationId }: { conversationId: string }) {
  const typing = useChatStore((s) => s.typing[conversationId]);
  const names = typing ? Object.values(typing) : [];
  if (names.length === 0) return null;
  const label =
    names.length === 1 ? `${names[0]} is typing…` : `${names.length} people are typing…`;
  return (
    <div className="flex items-end gap-2.5" aria-label={label}>
      <span
        className="bg-accent text-info font-heading flex size-[30px] shrink-0 items-center justify-center rounded-[9px] text-[11px] font-bold"
        aria-hidden
      >
        {initials(names[0] ?? "")}
      </span>
      <div className="bg-card flex items-center gap-1 rounded-[16px_16px_16px_4px] border px-4 py-3">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="bg-muted-foreground/60 size-[7px] animate-pulse rounded-full motion-reduce:animate-none"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function Composer({ conversationId, title }: { conversationId: string; title: string }) {
  const send = useChatStore((s) => s.send);
  const sendTyping = useChatStore((s) => s.sendTyping);
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const typingActive = useRef(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset composer + typing state when switching conversations (and on unmount),
  // telling the server we've stopped typing so peers don't wait out the TTL.
  useEffect(() => {
    setValue("");
    typingActive.current = false;
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (typingActive.current) {
        typingActive.current = false;
        sendTyping(conversationId, false);
      }
    };
  }, [conversationId, sendTyping]);

  function signalTyping() {
    if (!typingActive.current) {
      typingActive.current = true;
      sendTyping(conversationId, true);
    }
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(stopTyping, 2500);
  }

  function stopTyping() {
    if (typingActive.current) {
      typingActive.current = false;
      sendTyping(conversationId, false);
    }
  }

  async function submit() {
    const body = value.trim();
    if (!body || sending) return;
    setSending(true);
    stopTyping();
    try {
      await send(conversationId, body);
      setValue("");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-card flex flex-col gap-2 border-t p-4">
      <div className="flex items-end gap-2 rounded-2xl border px-3.5 py-2">
        <Textarea
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            signalTyping();
          }}
          onBlur={stopTyping}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={`Message ${title}…`}
          rows={1}
          className="max-h-40 min-h-9 flex-1 resize-none border-0 bg-transparent px-0 py-1.5 shadow-none focus-visible:ring-0"
        />
        <Button type="button" size="icon" onClick={() => void submit()} disabled={!value.trim() || sending}>
          <Send className="size-4" />
        </Button>
      </div>
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-2 px-1.5 text-[11px]">
        <span>Enter to send · Shift + Enter for a new line</span>
        <span className="inline-flex items-center gap-1.5">
          <Lock className="text-success size-3" />
          Internal only — clients never see team chat
        </span>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  mine,
  canManage,
  isGroup,
  conversationId,
}: {
  message: Message;
  mine: boolean;
  canManage: boolean;
  isGroup: boolean;
  conversationId: string;
}) {
  const editMessage = useChatStore((s) => s.editMessage);
  const deleteMessage = useChatStore((s) => s.deleteMessage);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canEdit = mine && !message.isDeleted;
  const canDelete = (mine || canManage) && !message.isDeleted;

  async function saveEdit() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await editMessage(conversationId, message.id, body);
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't edit the message.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await deleteMessage(conversationId, message.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't delete the message.");
      setBusy(false); // on success the bubble tombstones via WS; keep it disabled
    }
  }

  // Small sender avatar on incoming messages (not on mine), bottom-aligned.
  const avatar = !mine ? (
    <span
      className="bg-accent text-info font-heading flex size-[30px] shrink-0 items-center justify-center self-end rounded-[9px] text-[11px] font-bold"
      aria-hidden
    >
      {initials(message.senderName)}
    </span>
  ) : null;

  if (message.isDeleted) {
    return (
      <div className={cn("flex w-full", mine ? "justify-end" : "justify-start")}>
        <div
          className={cn("flex max-w-[82%] items-end gap-2.5", mine ? "flex-row-reverse" : "flex-row")}
        >
          {avatar}
          <div className="text-muted-foreground bg-muted/50 rounded-2xl px-3.5 py-2 text-sm italic">
            This message was deleted
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("group flex w-full", mine ? "justify-end" : "justify-start")}>
      <div className={cn("flex max-w-[82%] items-end gap-2.5", mine ? "flex-row-reverse" : "flex-row")}>
        {avatar}
        <div className={cn("flex min-w-0 flex-col gap-1", mine ? "items-end" : "items-start")}>
          {!mine && isGroup && (
            <span className="text-muted-foreground px-1 text-xs font-medium">
              {message.senderName}
            </span>
          )}
          {editing ? (
            <div className="flex w-72 max-w-full flex-col gap-1">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void saveEdit();
              }
              if (e.key === "Escape") setEditing(false);
            }}
            rows={2}
            className="resize-none text-sm"
            autoFocus
          />
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={() => void saveEdit()} disabled={busy}>
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className={cn("flex items-center gap-1", mine ? "flex-row-reverse" : "flex-row")}>
          <div
            className={cn(
              "min-w-0 px-3.5 py-2 text-sm leading-relaxed break-words whitespace-pre-wrap",
              mine
                ? "bg-brand-panel rounded-[16px_16px_4px_16px] text-white"
                : "bg-card text-foreground rounded-[16px_16px_16px_4px] border",
            )}
          >
            {message.body}
          </div>
          {(canEdit || canDelete) && (
            // Always-visible (not hover-only) kebab so message actions are
            // discoverable and usable on touch/keyboard, not just mouse-hover.
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Message actions"
                  disabled={busy}
                  className="text-muted-foreground hover:text-foreground rounded p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 disabled:opacity-40 data-[state=open]:opacity-100"
                >
                  <MoreHorizontal className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={mine ? "end" : "start"} className="w-36">
                {canEdit && (
                  <DropdownMenuItem
                    onSelect={() => {
                      setDraft(message.body);
                      setEditing(true);
                    }}
                  >
                    <Pencil className="size-3.5" />
                    Edit
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={busy}
                    onSelect={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="size-3.5" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this message?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the message for everyone in the
                  conversation. This can&apos;t be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => void remove()}
                  className="bg-destructive text-white hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
            </div>
          )}
          <span className="text-muted-foreground px-1 text-[11px] tabular-nums">
            {formatTime(message.createdAt)}
            {message.editedAt && " · edited"}
          </span>
        </div>
      </div>
    </div>
  );
}

export function MessageThread({ conversation }: { conversation: Conversation }) {
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const messages = useChatStore((s) => s.messagesByConv[conversation.id]);
  const pageMeta = useChatStore((s) => s.msgPage[conversation.id]);
  const loadOlder = useChatStore((s) => s.loadOlder);
  const isOnline = useChatStore((s) => s.isOnline);
  const leaveConversation = useChatStore((s) => s.leaveConversation);
  const deleteConversation = useChatStore((s) => s.deleteConversation);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | "leave" | "delete">(null);

  const title = conversationTitle(conversation, meId);
  const other = otherMember(conversation, meId);
  const online = conversation.kind === "direct" && other ? isOnline(other.id) : false;
  const count = messages?.length ?? 0;
  const lastId = messages?.[messages.length - 1]?.id;
  const isGroup = conversation.kind === "group";

  const myRole: MemberRole =
    conversation.members.find((m) => String(m.id) === String(meId))?.role ?? "member";
  const canManage = isGroup && (myRole === "owner" || myRole === "admin");
  const isOwner = isGroup && myRole === "owner";

  // Auto-scroll to the newest message when a message arrives at the tail or the
  // conversation changes — NOT when "load earlier" prepends older messages
  // (which grows count but leaves the last id unchanged), so history-loading
  // doesn't yank the viewport back to the bottom.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [lastId, conversation.id]);

  async function confirmAction() {
    try {
      if (confirm === "leave") await leaveConversation(conversation.id);
      else if (confirm === "delete") await deleteConversation(conversation.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setConfirm(null);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-3 border-b px-5 py-3.5">
        <div className="relative">
          <span
            className={cn(
              "font-heading flex size-[42px] items-center justify-center rounded-sm text-sm font-bold",
              isGroup ? "bg-brand-panel text-white" : "bg-accent text-info",
            )}
          >
            {isGroup ? <Users className="size-[18px]" /> : initials(title)}
          </span>
          {conversation.kind === "direct" && (
            <PresenceDot online={online} className="absolute -right-0.5 -bottom-0.5 size-3" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-[15px] font-semibold">{title}</p>
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
            {isGroup ? (
              `${conversation.members.length} members`
            ) : online ? (
              <>
                <span className="bg-success size-1.5 rounded-full" />
                Online
              </>
            ) : (
              "Offline"
            )}
          </p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="icon" variant="ghost" aria-label="Conversation options">
              <MoreVertical className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {isGroup && (
              <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
                Group settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setConfirm("leave")}>
              {isGroup ? "Leave group" : "Leave conversation"}
            </DropdownMenuItem>
            {isOwner && (
              <DropdownMenuItem variant="destructive" onSelect={() => setConfirm("delete")}>
                Delete group
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="bg-muted/30 flex-1 space-y-3.5 overflow-y-auto px-5 py-5">
        {pageMeta?.nextPage !== null && count > 0 && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void loadOlder(conversation.id)}
              disabled={pageMeta?.loading}
            >
              Load earlier messages
            </Button>
          </div>
        )}
        {messages === undefined ? (
          <p className="text-muted-foreground text-center text-sm">Loading…</p>
        ) : count === 0 ? (
          <p className="text-muted-foreground text-center text-sm">
            No messages yet. Say hello!
          </p>
        ) : (
          messages.map((m, i) => {
            const prev = messages[i - 1];
            const showDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
            return (
              <div key={m.id} className="space-y-3.5">
                {showDay && <DayDivider label={dayLabel(m.createdAt)} />}
                <MessageBubble
                  message={m}
                  mine={String(m.sender) === String(meId)}
                  canManage={canManage}
                  isGroup={isGroup}
                  conversationId={conversation.id}
                />
              </div>
            );
          })
        )}
        <TypingBubble conversationId={conversation.id} />
        <div ref={bottomRef} />
      </div>

      <Composer conversationId={conversation.id} title={title} />

      {isGroup && (
        <GroupSettingsDialog
          conversation={conversation}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      )}

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "delete" ? "Delete this group?" : "Leave this conversation?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? "This permanently deletes the group and all its messages for everyone."
                : isGroup
                  ? "You'll stop receiving messages from this group."
                  : "It will be hidden from your list until the other person messages you again."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={confirm === "delete" ? "destructive" : "default"}
              onClick={() => void confirmAction()}
            >
              {confirm === "delete" ? "Delete" : "Leave"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
